import { useStable } from "../context/StableContext";
import { NAV_ITEMS, NAV_GROUPS } from "../config/navigationConfig";

// Minimal capability adapter over the EXISTING role/perms/assist model.
// Reproduces exactly the current visibility rules — it is NOT a permission engine.
function canView(capability, ctx) {
  const { role, assist, isPlatformAdmin } = ctx;
  switch (capability) {
    case "opsNonOwner":
      return assist || role !== "owner";
    case "manage":
      return assist || ["manager", "admin"].includes(role);
    case "platformAdmin":
      return !!isPlatformAdmin;
    case "always":
    default:
      return true;
  }
}

// Shared resolver: reads StableContext and returns the visible, ordered items per surface.
// Desktop sidebar and mobile drawer BOTH derive from the same model (groups, order, icons,
// nomenclature, capability) — no per-surface duplication.
export function useNavigation() {
  const { role, assist, perms } = useStable();
  const ctx = { role, assist, isPlatformAdmin: perms?.isPlatformAdmin };
  const visible = (item) => canView(item.capability, ctx);

  const navItems = NAV_ITEMS
    .filter((i) => i.desktop && visible(i))
    .sort((a, b) => a.order - b.order);

  const tabItems = NAV_ITEMS
    .filter((i) => i.mobileTab != null && visible(i))
    .sort((a, b) => a.mobileTab - b.mobileTab);

  // Main groups: rendered with headings in the scrollable module list.
  const mainGroups = NAV_GROUPS
    .filter((g) => g.zone === "main")
    .map((g) => ({ ...g, items: navItems.filter((i) => i.group === g.key) }))
    .filter((g) => g.items.length > 0);

  // Utility items: pinned to the lower/system area (Impostazioni), no heading.
  const utilityKeys = NAV_GROUPS.filter((g) => g.zone === "utility").map((g) => g.key);
  const utilityItems = navItems.filter((i) => utilityKeys.includes(i.group));

  return { navItems, mainGroups, utilityItems, tabItems };
}
